import React from "react";

import { faRepeat } from "@fortawesome/pro-duotone-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  useRouteMatch,
} from "react-router-dom";

import { SaleAction, setSetting } from "actions";

import AuditLogModalAdapter from "components/AuditLog/adapter";
import BillingDocumentActivityModalAdapter from "components/Billing/BillingDocument/Activity/Modal/Adapter";
import { BillingDocumentModalAdapter } from "components/Billing/BillingDocument/Modal";
import { BillingRunModalAdapter } from "components/Billing/BillingRun/Modal";
import { LedgerEntryModalAdapter } from "components/Billing/LedgerEntry/Modal";
import { BillingDocumentReviewModal } from "components/BillingWorkFlow/BillingDocumentReviewModal";
import { BulkUpdateBusinessModal } from "components/BulkUpdateOptionalFieldsModal/Businesses";
import EditBusiness from "components/Business/BusinessForm/BusinessForm";
import BusinessRelationModalAdapter from "components/Business/BusinessRelations/Modal/adapter";
import GlobalBusinessCreateModalAdapter from "components/Business/GlobalBusinessCreate/adapter";
import { ManageBusinesses } from "components/Business/ManageBusinesses";
import { BusinessReportsModal } from "components/Business/Reports";
import { IconTextButton } from "components/Button";
import CommentsModalAdapter from "components/Comments/Modal/adapter";
import ConsignToSaleModalAdapter from "components/ConsignToSaleModal/Adapter";
import CreateExternalAgentSaleFromSaleAdapter from "components/CreateExternalAgentSaleFromSaleModal/Adapter";
import { CreateSellViaExternalAgentSaleFromSaleAdapter } from "components/CreateSellViaExternalAgentSaleFromSaleModal/Adapter";
import DeploymentSaleOptionsAdapter from "components/DeploymentSaleOptions/adapter";
import { DevicePlayground } from "components/DevicePlayground";
import { EContractModal } from "components/EContracts/eContractModal";
import { FilterModalAdapter } from "components/Filter/Modal/Adapter";
import { HashRoute } from "components/HashRoute";
import { ImportSummaryAdapter } from "components/Importer/ImporterSummaryAdatper";
import { Inbox } from "components/Inbox";
import { CreateIntegrationBusinessModalAdapter } from "components/IntegrationBusinesses/CreateModal";
import { EditIntegrationBusinessModal } from "components/IntegrationBusinesses/Modal";
import { Column, Row } from "components/Layout";
import { LedgerAccountModalAdapter } from "components/LedgerAccount/Modal";
import LoadingSpinner from "components/LoadingSpinner";
import { LoadTable } from "components/Loads";
import MapBusinessModal from "components/MapBusinessModal";
import MessageBox from "components/MessageBox";
import { StateModalAdapter } from "components/Modal/DispatchingAdapter";
import { InterestModalAdapter } from "components/MoneyTable/InterestModal";
import { MoneyTableWrapper } from "components/MoneyTable/MoneyTable";
import { MultiSaleReportsModal } from "components/MultiSaleReports";
import { EditNominationModal } from "components/Nominations/Modal";
import { NLISReceiptModalAdapter } from "components/ReceiptModals/NLISReceiptModalAdapter";
import EmailReportModalAdapter from "components/EmailReportModal/adapter";
import EmailReportJobModalAdapter from "components/ReportJob/SendEmailModal/adapter";
import { ReportJobList } from "components/ReportJobList/ReportJobList";
import { RetainProceedsModalAdapter } from "components/RetainProceeds/Modal/Adapter";
import { RuleBookModalAdapter } from "components/RuleBookTable/modal";
import { RuleSetModal } from "components/RuleSets/Modal";
import { MoveToExistingRuleSetModal } from "components/RuleSets/Modal/MoveToExistingRuleSetModal";
import RuleTesterModal from "components/RuleTester";
import { BusinessLabelModal } from "components/BusinessLabel/Modal";
import { SaleStatusModalAdapter } from "components/SaleStatus";
import { SelectSyncDataAdapter } from "components/SelectSyncData/Adapter";
import { SundryTemplateModalAdapter } from "components/SundryTemplate";
import { BuyerSplitModalAdapter } from "components/TransactionSplits/BuyerSplit/BuyerSplitModalAdapter";
import VendorSplitModalAdapter from "components/TransactionSplits/NewVendorSplitModal/adapter";
import EditUserRoleModalAdapter from "components/UserRoles/Modal/adapter";

import { ModalTypes } from "constants/navigation";

import { SaleTypePickerModal } from "components/SaleTypePickerModal";
import LivestockSales from "containers/LivestockSales";
import SaleModalAdapter from "containers/SaleModal/Adapter";
import { useGetCurrentSaleEntryRoute } from "containers/SaleyardHome/useGetCurrentSaleEntryRoute";
import Settings from "containers/Settings";
import { RuleLibraryModalAdapter } from "containers/Settings/RuleBooks/Library/Adapter";
import { RuleModal } from "containers/Settings/RuleBooks/Rules/Modal";
import { MasterRuleModal } from "containers/Settings/RuleBooks/Rules/Modal/MasterRule";
import { MasterRuleSetModal } from "containers/Settings/RuleBooks/Rules/Modal/MasterRuleSet";
import SingleWeighSettingsModalAdapter from "containers/SingleWeighSettingsModal/Adapter";
import { UserDashboard } from "containers/UserDashboard";
import UserOverview from "containers/UserOverview";

import {
  getDashboardRoute,
  getInboxRoute,
  getLoadsRoute,
  getManageBusinessRoute,
  getMoneyTableRoute,
  getReportJobList,
  getSettingsRoute,
  getUserOverviewRoute,
} from "lib/navigation";

import {
  getIsBusinessUser,
  getIsFetchingSales,
  getIsStaff,
  getIsTransporter,
  getSetting,
} from "selectors";

import { useIsMobile } from "hooks";
import { MobileTransactionalCreateSaleModal } from "components/MobileTransactionalModal";

const NoSalesText = ({ isBusinessUser = false, followOn = false }) =>
  isBusinessUser ? null : (
    <Row justifyCenter textAlignCenter>
      {followOn ? "Or get" : "Get"} started by adding a new sale with the
      &quot;New Sale Day&quot; button in the menu.
    </Row>
  );

export function SaleyardRoutes(props) {
  const { loadedSaleId, hasNoSales } = props;

  const match = useRouteMatch();
  const isBusinessUser = useSelector(getIsBusinessUser);
  const isStaff = useSelector(getIsStaff);
  const allSalesLoaded = useSelector(getSetting(Settings.allSalesLoaded));
  const isUpdatingSales = useSelector(getIsFetchingSales);
  const isTransporter = useSelector(getIsTransporter);

  const isMobile = useIsMobile();

  const dispatch = useDispatch();

  const onLoadMoreSales = () => {
    dispatch(setSetting(Settings.allSalesLoaded, true));
    dispatch(SaleAction.request({ fetchAll: true }));
  };

  const { saleyard } = match.params;

  const currentSaleEntryRoute = useGetCurrentSaleEntryRoute(loadedSaleId);

  const renderNoneOrMostRecent = () => {
    if (isTransporter) {
      // Transporters don't have a saleyard home, they go straight to loads
      return <Redirect to={getLoadsRoute()} />;
    } else if (isUpdatingSales) {
      return (
        <MessageBox>
          <LoadingSpinner subjectName="Sales" size={24} />
        </MessageBox>
      );
    } else if (hasNoSales) {
      const ButtonWrapper = isMobile ? Column : Row;
      return (
        <MessageBox>
          <Column alignCenter>
            <Row justifyCenter>There are no sales available to display.</Row>

            {allSalesLoaded ? (
              <NoSalesText isBusinessUser={isBusinessUser} />
            ) : (
              <>
                <ButtonWrapper alignCenter justifyCenter textAlignCenter>
                  Only the last 3 months of sales are loaded - click to{" "}
                  <IconTextButton
                    icon={faRepeat}
                    iconSize={25}
                    textSize={16}
                    onClick={onLoadMoreSales}
                  >
                    load more sales
                  </IconTextButton>
                </ButtonWrapper>
                <NoSalesText isBusinessUser={isBusinessUser} followOn />
              </>
            )}
          </Column>
        </MessageBox>
      );
    } else if (!loadedSaleId) {
      return (
        <MessageBox>
          Please select the sale you would like to work with today.
        </MessageBox>
      );
    } else if (loadedSaleId) {
      return <Redirect to={currentSaleEntryRoute} />;
    }
  };

  const onUserOverviewPage =
    useLocation().pathname === getUserOverviewRoute() &&
    match.path === getUserOverviewRoute();

  // When loading sales on the user overview page, we're probably going to
  // be pushing off to a saleyard after this, so show loading.
  if (onUserOverviewPage && isUpdatingSales) {
    return (
      <MessageBox>
        <LoadingSpinner subjectName="Sales" size={24} />
      </MessageBox>
    );
  }

  return (
    <>
      <Switch>
        {isStaff && (
          <Route exact path="/devices/" component={DevicePlayground} />
        )}
        {/* Specific direct routes */}
        <Route exact path={getDashboardRoute()} component={UserDashboard} />
        <Route path={getInboxRoute()} component={Inbox} />

        <Route
          exact
          path={getMoneyTableRoute()}
          component={MoneyTableWrapper}
        />
        <Route exact path={getReportJobList()} component={ReportJobList} />
        <Route path={getManageBusinessRoute()} component={ManageBusinesses} />
        <Route path={getSettingsRoute()} component={Settings} />
        {/* A specific sale */}
        <Route path={`${match.path}/sale/:saleId`} component={LivestockSales} />
        {/* The role picker */}
        <Route path={getUserOverviewRoute()} exact component={UserOverview} />
        {/* The transporter loads page */}
        <Route path={getLoadsRoute()} exact component={LoadTable} />
        {/* "Please select the sale you would like to work with today */}
        <Route exact path={match.path} render={renderNoneOrMostRecent} />
      </Switch>
      <HashRoute
        component={MultiSaleReportsModal}
        hash={ModalTypes.MultiSaleReports}
      />
      <HashRoute
        component={BusinessReportsModal}
        hash={ModalTypes.BusinessReports}
      />
      <HashRoute
        component={BillingDocumentModalAdapter}
        hash={ModalTypes.EditBillingDocument}
      />
      <HashRoute
        component={BillingDocumentActivityModalAdapter}
        hash={ModalTypes.BillingDocumentActivity}
      />
      <HashRoute component={RuleBookModalAdapter} hash={ModalTypes.RuleBook} />
      <HashRoute
        component={BillingRunModalAdapter}
        hash={ModalTypes.EditBillingRun}
      />
      <HashRoute
        component={LedgerAccountModalAdapter}
        hash={ModalTypes.EditLedgerAccount}
      />
      <HashRoute
        component={CreateExternalAgentSaleFromSaleAdapter}
        hash={ModalTypes.CreateExternalAgentSaleFromSale}
      />

      <HashRoute
        component={CreateSellViaExternalAgentSaleFromSaleAdapter}
        hash={ModalTypes.CreateSellViaExternalAgentSaleFromSale}
      />

      <HashRoute
        component={SundryTemplateModalAdapter}
        hash={ModalTypes.SundryTemplate}
      />

      <StateModalAdapter
        component={BusinessLabelModal}
        hash={ModalTypes.BusinessLabel}
      />

      <HashRoute
        component={SaleStatusModalAdapter}
        hash={ModalTypes.SaleStatus}
      />

      <HashRoute
        component={LedgerEntryModalAdapter}
        hash={ModalTypes.EditLedgerEntry}
      />
      <HashRoute
        component={GlobalBusinessCreateModalAdapter}
        hash={ModalTypes.GlobalBusinessCreate}
      />
      <HashRoute
        component={MapBusinessModal}
        hash={ModalTypes.MapBusiness}
        componentProps={{ saleyard }}
      />
      <HashRoute
        component={SaleModalAdapter}
        hash={ModalTypes.Sale}
        componentProps={{ saleyard }}
      />
      <HashRoute
        component={SingleWeighSettingsModalAdapter}
        hash={ModalTypes.SingleWeighSettings}
        componentProps={{ saleyard }}
      />
      <HashRoute
        component={ConsignToSaleModalAdapter}
        hash={ModalTypes.ConsignToSale}
      />
      <HashRoute component={CommentsModalAdapter} hash={ModalTypes.Comments} />
      <HashRoute component={AuditLogModalAdapter} hash={ModalTypes.AuditLog} />
      <HashRoute
        component={NLISReceiptModalAdapter}
        hash={ModalTypes.NLISTransferReceipt}
      />
      <HashRoute
        component={DeploymentSaleOptionsAdapter}
        hash={ModalTypes.DeploymentSaleOptions}
      />
      <HashRoute component={EContractModal} hash={ModalTypes.EContract} />
      <HashRoute
        component={EditUserRoleModalAdapter}
        hash={ModalTypes.EditUserRole}
      />
      <StateModalAdapter
        component={EditIntegrationBusinessModal}
        hash={ModalTypes.EditIntegrationBusiness}
      />
      <HashRoute
        component={CreateIntegrationBusinessModalAdapter}
        hash={ModalTypes.CreateIntegrationBusiness}
      />
      <StateModalAdapter
        component={EditNominationModal}
        hash={ModalTypes.EditNomination}
      />
      <HashRoute
        component={SelectSyncDataAdapter}
        hash={ModalTypes.SelectSyncData}
      />
      <HashRoute
        component={BillingDocumentReviewModal}
        hash={ModalTypes.BillingDocumentReview}
      />

      <HashRoute component={RuleTesterModal} hash={ModalTypes.RuleTester} />
      <StateModalAdapter component={RuleModal} hash={ModalTypes.EditRule} />
      <StateModalAdapter
        component={RuleSetModal}
        hash={ModalTypes.EditRuleSet}
      />
      <StateModalAdapter
        component={MasterRuleSetModal}
        hash={ModalTypes.EditMasterRuleSet}
      />
      <StateModalAdapter
        hash={ModalTypes.MoveToExistingRuleSet}
        component={MoveToExistingRuleSetModal}
      />
      <HashRoute
        component={VendorSplitModalAdapter}
        hash={ModalTypes.VendorSplit}
      />

      <HashRoute
        component={BuyerSplitModalAdapter}
        hash={ModalTypes.BuyerSplit}
      />
      <StateModalAdapter
        component={MasterRuleModal}
        hash={ModalTypes.EditMasterRule}
      />
      <StateModalAdapter
        component={RuleLibraryModalAdapter}
        hash={ModalTypes.RuleLibrary}
      />
      <HashRoute
        component={ImportSummaryAdapter}
        hash={ModalTypes.ImportSummary}
      />
      <HashRoute component={InterestModalAdapter} hash={ModalTypes.Interest} />
      <StateModalAdapter
        component={RetainProceedsModalAdapter}
        hash={ModalTypes.RetainProceeds}
      />

      <HashRoute
        component={EmailReportModalAdapter}
        hash={ModalTypes.EmailReportModal}
      />
      <HashRoute
        component={EmailReportJobModalAdapter}
        hash={ModalTypes.EmailReportJobModal}
      />
      <HashRoute
        component={BusinessRelationModalAdapter}
        hash={ModalTypes.BusinessRelations}
      />
      <StateModalAdapter
        component={FilterModalAdapter}
        hash={ModalTypes.Filter}
      />
      <StateModalAdapter
        component={EditBusiness}
        hash={ModalTypes.EditBusiness}
      />
      <StateModalAdapter
        component={BulkUpdateBusinessModal}
        hash={ModalTypes.BulkUpdateBusiness}
      />
      <StateModalAdapter
        component={SaleTypePickerModal}
        hash={ModalTypes.SaleTypePicker}
      />
      <StateModalAdapter
        component={MobileTransactionalCreateSaleModal}
        hash={ModalTypes.MobileTransactionalCreateSaleModal}
      />
      <HashRoute>
        component={/* TODO Add a component in here! */}
        hash={ModalTypes.UnmergeBusiness}
      </HashRoute>
    </>
  );
}
